import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ProductPicker } from "./ProductPicker";
import { errMsg } from "./ui";

const HINT = { "[^]": "100,199", "[=]": "one or several, comma separated", "[!]": "one value", "[~]": "abc · abc% · %abc",
  "[}]": "prefix", "[{]": "suffix", "[>]": "number", "[<]": "number" };

function initState(row, fields, plans) {
  const filt = Object.fromEntries((row?.filters || []).map((f) => [f.field, { op: f.op, value: f.value }]));
  const extra = (row?.filters || []).filter((f) => !fields.includes(f.field)).map((f) => f.field);
  const pick = Object.fromEntries(plans.map((pl) => [pl, ""]));
  (row?.products || []).forEach((p) => { if (p.plan in pick) pick[p.plan] = p.id; });
  return { priority: row ? String(row.priority) : "0", filt, fieldList: [...fields, ...extra], pick };
}

function ConditionRow({ field, cond, ops, onChange }) {
  const op = cond?.op || "";
  return (
    <div className="grid grid-cols-[140px_150px_1fr] items-center gap-2" data-testid={`bs-cond-${field}`}>
      <span className="truncate font-mono text-xs text-slate-600" title={field}>{field}</span>
      <select value={op} onChange={(e) => onChange(e.target.value ? { op: e.target.value, value: cond?.value || "" } : null)}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid={`bs-cond-op-${field}`}>
        <option value="">Ignore</option>
        {Object.entries(ops).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <Input value={cond?.value || ""} disabled={!op} placeholder={op ? HINT[op] || "" : ""} className="h-8 font-mono text-xs"
             onChange={(e) => onChange({ op, value: e.target.value })} data-testid={`bs-cond-val-${field}`} />
    </div>
  );
}

export function RuleDialog({ row, meta, catalog, onClose, onSaved }) {
  const ops = useMemo(() => ({ ...meta.operators, ...meta.legacy_operators }), [meta]);
  const [s, setS] = useState(() => initState(row, meta.fields, meta.plans));
  const [busy, setBusy] = useState(false);
  const multi = meta.plans.filter((pl) => (row?.products || []).filter((p) => p.plan === pl).length > 1);
  const kept = (row?.products || []).filter((p) => !meta.plans.includes(p.plan));
  const save = async () => {
    const filters = s.fieldList.filter((f) => s.filt[f]).map((f) => ({ field: f, ...s.filt[f] }));
    const body = { priority: Number(s.priority), filters, product_ids: meta.plans.map((pl) => s.pick[pl]).filter(Boolean), base: row?.base };
    setBusy(true);
    try {
      if (row) await api.put(`/billing-sync/mappings/${row.id}`, body); else await api.post("/billing-sync/mappings", body);
      toast.success(row ? `Rule #${row.id} saved to the billing table` : "Rule added to the billing table"); onSaved();
    } catch (e) { toast.error(errMsg(e, "Could not save the rule")); if (e.response?.status === 409) onSaved(); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="bs-rule-dialog">
        <DialogHeader>
          <DialogTitle>{row ? `Edit rule #${row.id}` : "New mapping rule"}</DialogTitle>
          <DialogDescription>Every condition you set must match. Lower priority runs first; the first matching rule decides the product.</DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-3 text-sm"><span className="w-[140px] text-xs font-semibold uppercase tracking-wide text-slate-500">Priority</span>
          <Input type="number" value={s.priority} onChange={(e) => setS({ ...s, priority: e.target.value })} className="h-9 w-28" data-testid="bs-rule-priority" /></label>
        <section className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Platform match criteria</div>
          {s.fieldList.map((f) => <ConditionRow key={f} field={f} cond={s.filt[f]} ops={ops}
            onChange={(c) => setS({ ...s, filt: { ...s.filt, [f]: c } })} />)}
        </section>
        <section className="space-y-2 rounded-lg border border-slate-200 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bills to — one Stax Bill product per plan</div>
          {meta.plans.map((pl) => <ProductPicker key={pl} plan={pl} value={s.pick[pl]} testid={`bs-pick-${pl.replace(/\s/g, "-").toLowerCase()}`}
            products={(catalog?.products || []).filter((p) => p.plan === pl)} onChange={(v) => setS({ ...s, pick: { ...s.pick, [pl]: v } })} />)}
          <p className="text-[11px] text-slate-500">Account Charges is used when the extension's plan has no product on this rule.</p>
          {multi.length > 0 && <p className="text-[11px] text-amber-700" data-testid="bs-rule-multi">This rule lists more than one product for {multi.join(", ")}; only the last one was ever billed, and saving keeps just that one.</p>}
          {kept.length > 0 && <p className="text-[11px] text-slate-500">Also kept as is: {kept.map((p) => `${p.label} (${p.id})`).join(", ")}</p>}
        </section>
        <DialogFooter>
          <span className="mr-auto self-center text-[11px] text-amber-700">Saving writes straight to the live billing table.</span>
          <Button variant="outline" onClick={onClose} data-testid="bs-rule-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="bs-rule-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save rule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
